/*
 * product-gallery-lazy.js — 04.09.2026
 *
 * Гідратор відкладених слайдів галереї товару.
 *
 * Працює у парі зі snippets/product-thumbnail.liquid: той віддає для
 * слайдів після шостого порожній <div class="product__media product__media--deferred">
 * із збереженим aspect-ratio і трьома data-атрибутами замість повного <img>
 * з 12-варіантним srcset. Цей файл створює справжній <img> тоді, коли слайд
 * наближається до в'юпорта.
 *
 * Навіщо: у товарів по 40–105 фото. До зміни секція малювала ~2 КБ HTML
 * на кожен слайд незалежно від того, чи побачить його користувач.
 *
 * Тригери гідратації:
 *   1. IntersectionObserver (rootMargin 300px) — основний. Swiper рухає
 *      слайди через transform, IO це коректно відстежує.
 *   2. Подія slideChange у Swiper — страхувальна сітка: гідрує активний
 *      слайд і два наступні/попередні одразу, без очікування IO.
 *   3. Клік по мініатюрі або по стрілках — те саме.
 *   4. Якщо IntersectionObserver недоступний — гідрує все після load.
 *
 * 05.09.2026: додано підтримку мініатюр (data-media-kind="thumb" зі
 * snippets/product-gallery.liquid — srcset 110/220/330, sizes 110px) і
 * вставку іконки зуму (span.product__media-icon) для головних слайдів,
 * бо відкладена гілка product-thumbnail.liquid її більше не рендерить.
 *
 * Якщо треба вимкнути: підняти eager_slides у product-thumbnail.liquid
 * і thumb_eager у product-gallery.liquid до 999 — тоді відкладених
 * елементів не буде взагалі, а цей скрипт просто нічого не знайде.
 */
(function () {
  'use strict';

  var WIDTHS = [246, 493, 600, 713, 823, 990, 1100, 1206, 1346, 1426, 1646, 1946];
  var SIZES =
    '(min-width: 1100px) calc((100vw - 10rem) / 2), (min-width: 750px) calc((100vw - 10rem) / 2), calc(100vw - 4rem)';
  var SELECTOR = '.product__media--deferred';
  var THUMB_WIDTHS = [110, 220, 330];
  var THUMB_SIZES = '110px';
  var ICON_HTML =
    '<svg class="icon icon-plus" width="14" height="15" viewBox="0 0 14 15" fill="none" xmlns="http://www.w3.org/2000/svg">' +
    '<path d="M6 6.5V0.5H8V6.5H14V8.5H8V14.5H6V8.5H0V6.5H6Z" fill="currentColor"/></svg>';

  function withWidth(url, w) {
    return /[?&]width=\d+/.test(url)
      ? url.replace(/([?&])width=\d+/, '$1width=' + w)
      : url + (url.indexOf('?') === -1 ? '?' : '&') + 'width=' + w;
  }

  function hydrate(box) {
    if (!box || box.dataset.hydrated === '1') return;
    var base = box.dataset.mediaSrc;
    if (!base) return;
    box.dataset.hydrated = '1';

    var isThumb = box.dataset.mediaKind === 'thumb';
    var widths = isThumb ? THUMB_WIDTHS : WIDTHS;
    var img = document.createElement('img');
    var set = [];
    for (var i = 0; i < widths.length; i++) {
      set.push(withWidth(base, widths[i]) + ' ' + widths[i] + 'w');
    }
    img.setAttribute('srcset', set.join(', '));
    img.setAttribute('sizes', isThumb ? THUMB_SIZES : SIZES);
    img.setAttribute('src', base);
    img.setAttribute('alt', box.dataset.mediaAlt || '');
    img.setAttribute('loading', 'lazy');
    img.setAttribute('decoding', 'async');
    if (box.dataset.mediaW && box.dataset.mediaH) {
      img.setAttribute('width', box.dataset.mediaW);
      img.setAttribute('height', box.dataset.mediaH);
    }
    box.appendChild(img);
    box.classList.remove('product__media--deferred');

    /* Іконка зуму для головного слайда: у розмітці її немає, щоб не тягнути
       SVG на кожен із 80 відкладених слайдів. */
    if (!isThumb) {
      var opener = box.parentElement;
      if (opener && opener.tagName === 'MODAL-OPENER' && !opener.querySelector('.product__media-icon')) {
        var icon = document.createElement('span');
        icon.className = 'product__media-icon motion-reduce';
        icon.setAttribute('aria-hidden', 'true');
        icon.innerHTML = ICON_HTML;
        opener.insertBefore(icon, box);
      }
    }
  }

  function hydrateAll(root) {
    var list = (root || document).querySelectorAll(SELECTOR);
    for (var i = 0; i < list.length; i++) hydrate(list[i]);
  }

  /* Гідрує слайд за індексом і сусідні в межах radius. */
  function hydrateAround(swiperEl, index, radius) {
    if (!swiperEl) return;
    var slides = swiperEl.querySelectorAll('.swiper-slide');
    var from = Math.max(0, index - radius);
    var to = Math.min(slides.length - 1, index + radius);
    for (var i = from; i <= to; i++) {
      if (slides[i]) hydrateAll(slides[i]);
    }
  }

  var observer = null;
  if ('IntersectionObserver' in window) {
    observer = new IntersectionObserver(
      function (entries) {
        for (var i = 0; i < entries.length; i++) {
          if (entries[i].isIntersecting) {
            hydrate(entries[i].target);
            observer.unobserve(entries[i].target);
          }
        }
      },
      { rootMargin: '300px' }
    );
  }

  function observeAll() {
    var list = document.querySelectorAll(SELECTOR);
    if (!list.length) return;
    if (!observer) {
      hydrateAll();
      return;
    }
    for (var i = 0; i < list.length; i++) observer.observe(list[i]);
  }

  /* Підключаємо страхувальні тригери до слайдерів галереї. */
  function bindSliders() {
    var lists = document.querySelectorAll('.js-media-list');
    for (var i = 0; i < lists.length; i++) {
      (function (el) {
        if (el.dataset.lazyBound === '1') return;
        el.dataset.lazyBound = '1';

        var attach = function () {
          if (!el.swiper) return false;
          el.swiper.on('slideChange', function () {
            hydrateAround(el, this.activeIndex, 2);
          });
          el.swiper.on('touchStart', function () {
            hydrateAround(el, this.activeIndex, 3);
          });
          hydrateAround(el, el.swiper.activeIndex || 0, 2);
          return true;
        };

        if (!attach()) {
          /* Swiper ініціалізується в global.js уже після цього файлу —
             чекаємо появи інстансу, але не довше двох секунд. */
          var tries = 0;
          var timer = setInterval(function () {
            if (attach() || ++tries > 20) clearInterval(timer);
          }, 100);
        }
      })(lists[i]);
    }

    /* Мініатюри й стрілки: клік майже завжди означає перехід на слайд,
       який IO ще не встиг підхопити. */
    document.addEventListener(
      'click',
      function (e) {
        var t = e.target;
        if (!t || !t.closest) return;
        if (
          t.closest('.product__media-subitem') ||
          t.closest('.product__button-next') ||
          t.closest('.product__button-prev')
        ) {
          var wrap = t.closest('.product__media-wrapper') || document;
          var list = wrap.querySelector('.js-media-list');
          if (list && list.swiper) hydrateAround(list, list.swiper.activeIndex, 4);
        }
      },
      { passive: true }
    );
  }

  /* Стрічка мініатюр: при прокрутці стрічки гідруємо активну область. */
  function bindThumbRails() {
    var rails = document.querySelectorAll('.js-media-sublist');
    for (var i = 0; i < rails.length; i++) {
      (function (el) {
        if (el.dataset.lazyBound === '1') return;
        el.dataset.lazyBound = '1';
        var attach = function () {
          if (!el.swiper) return false;
          el.swiper.on('slideChange', function () {
            hydrateAround(el, this.activeIndex, 8);
          });
          el.swiper.on('touchEnd', function () {
            hydrateAround(el, this.activeIndex, 8);
          });
          return true;
        };
        if (!attach()) {
          var tries = 0;
          var timer = setInterval(function () {
            if (attach() || ++tries > 20) clearInterval(timer);
          }, 100);
        }
      })(rails[i]);
    }
  }

  function init() {
    observeAll();
    bindSliders();
    bindThumbRails();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  /* Перемальовка секції в редакторі теми. */
  document.addEventListener('shopify:section:load', function () {
    setTimeout(init, 50);
  });

  /* Відкриття модалки зуму — там своя гідратація, але слайди основного
     слайдера теж мають бути готові, бо модалка стартує з активного. */
  document.addEventListener('shopify:block:select', function () {
    setTimeout(init, 50);
  });

  window.hydrateProductGallery = hydrateAll;
})();
